<template>
  <div class="panel" data-qid="project-settings-card">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon icon="cog" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div class="profile-compact__text">
              {{ $t('dashboard.settings') }}
            </div>
          </div>
        </div>
      </div>

      <div class="profile-compact__actions">
        <span class="icon-btn"
              v-if="canEdit && hasReports && !editing"
              @click="startEditing"
              data-qid="project-settings-edit-button"
        >
          <font-awesome-icon icon="pen"/>
        </span>
        <span class="icon-btn btn-delete"
              v-if="editing"
              @click="editing = false"
              data-qid="project-settings-cancel-button"
        >
          <font-awesome-icon icon="rotate-left"/>
        </span>
        <span class="icon-btn btn-edit"
              v-if="editing"
              @click="save"
              data-qid="project-settings-save-button"
        >
          <font-awesome-icon icon="save"/>
        </span>
      </div>
    </header>

    <div class="panel__body">
      <!-- Fixed when the project is created -->
      <div class="engagement-fixed-settings" data-qid="project-settings-fixed">
        <div class="engagement-fixed-settings__hint">
          <font-awesome-icon icon="lock" class="mr-1"/>{{ $t('projects.settings_card.fixed_hint') }}
        </div>
        <div class="engagement-facts mb-0">
          <div class="engagement-facts__item">
            <span class="engagement-facts__label">{{ $t('table.type') }}</span>
            <span>{{ $t('projects.management.types.' + project.type) }}</span>
          </div>
          <div class="engagement-facts__item" v-if="project.assessment">
            <span class="engagement-facts__label">{{ $t('table.template') }}</span>
            <span>{{ project.assessment.name }}</span>
          </div>
          <div class="engagement-facts__item" v-if="project.scope">
            <span class="engagement-facts__label">{{ $t('table.scope') }}</span>
            <span>{{ $t('projects.launcher.scopes.' + project.scope) }}</span>
          </div>
          <div class="engagement-facts__item">
            <span class="engagement-facts__label">{{ $t('projects.create.schedule_label') }}</span>
            <span v-if="project.project">{{ $t('projects.schedule.' + $options.filters.slugify(project.schedule_type)) }}</span>
            <span v-else>{{ $t('projects.schedule.one_time') }}</span>
          </div>
        </div>
      </div>

      <div class="engagement-facts">
        <div class="engagement-facts__item">
          <span class="engagement-facts__label">{{ $t('table.last_updated') }}</span>
          <span>{{ project.updated_at | formatDate }}</span>
          <span v-if="project.updated_by" class="text-muted">· {{ project.updated_by.name }}</span>
        </div>
        <div class="engagement-facts__item" v-if="project.tags_translated && project.tags_translated.length">
          <span class="engagement-facts__label">{{ $t('table.tags') }}</span>
          <span class="badge" v-for="(tag, index) in project.tags_translated" :key="'tag-' + index">
            <font-awesome-icon icon="tag"/> {{ tag.name_translated | truncate(35) }}
          </span>
        </div>
        <!-- Only engagements are linked to another client now; an older link is still shown -->
        <div class="engagement-facts__item" v-if="linkedClient">
          <span class="engagement-facts__label">{{ $t('projects.create.external_link_label') }}</span>
          <span>
            <Avatar :object="linkedClient" size="xs"></Avatar>
            <span class="ml-1">{{ linkedClient.name }}</span>
          </span>
        </div>
      </div>

      <b-alert v-if="error.length"
               show
               variant="danger"
               class="mt-2"
      >{{ error }}</b-alert>

      <!-- Editable settings: the language of the report, for projects that have one -->
      <div class="engagement-settings-list" v-if="hasReports">
        <div class="engagement-settings-list__row">
          <label>{{ $t('projects.create.reporting_language') }}</label>
          <multiselect v-if="editing"
                       v-model="form.language"
                       :options="languages"
                       select-label=""
                       selected-label=""
                       deselect-label=""
                       label="text"
                       track-by="value"
                       :placeholder="$t('processes.create.language_placeholder')"
                       data-qid="project-settings-language"
          ></multiselect>
          <span v-else-if="currentLanguage">{{ currentLanguage.text }}</span>
          <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Avatar from '@/components/Avatar.vue';

// Settings of a one-time project that isn't an engagement (US 9478): what it was set up with, and its report language.
// Overdue reminders are part of the schedule; relations and links to other clients belong to engagements.
export default {
  name: 'ProjectSettingsCard',
  props: {
    project: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Avatar
  },
  data: function() {
    return {
      editing: false,
      error: '',
      form: {},
      languages: [
        {value: 'en', text: 'English'},
        {value: 'da', text: 'Danish'},
        {value: 'fr', text: 'French'},
        {value: 'de', text: 'German'},
        {value: 'sv', text: 'Swedish'},
        {value: 'nl', text: 'Dutch'},
        {value: 'es', text: 'Spanish'}
      ]
    }
  },
  computed: {
    // Only risk and BIA projects have a Reporting tab
    hasReports() {
      return this.project.type === 'risk_assessment' || this.project.type === 'bia_assessment';
    },
    linkedClient() {
      const client = this.project.additional_client;
      if (!client || !client.slug) {
        return null;
      }
      const currentClient = this.$store.state.client;
      return currentClient && currentClient.slug === client.slug && this.project.active_client ? this.project.active_client : client;
    },
    currentLanguage() {
      return _.find(this.languages, language => language.value === this.project.language) || null;
    }
  },
  methods: {
    startEditing() {
      this.error = '';
      this.form = { language: this.currentLanguage };
      this.editing = true;
    },
    save() {
      let self = this;
      piincHttp.put('projects/' + this.project.slug, {
        language: this.form.language ? this.form.language.value : null
      }).then(function() {
        self.editing = false;
        self.$toastr('success', self.$t('projects.update.success'));
        self.$emit('updated');
      }, function(error) {
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    }
  }
}
</script>
